import React, { useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@/components/ui/Tabs";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { NEWS, MEDIA, INSIGHTS } from "@/data/news";
import { useT } from "@/hooks/useT";

function ItemCard({ eyebrow, headline, meta, url, state, label, testId, readLabel }) {
  const isUnavailable = state === "unavailable";
  const isArchived = state === "archived";
  const stateLabel = (isArchived || isUnavailable) && label ? label : null;

  // Unavailable cards render a non-anchor card: outlet / headline / date
  // stay visible, but the href is removed and the "Read" affordance is
  // replaced by the state label.
  if (isUnavailable) {
    return (
      <div
        className="group flex h-full flex-col rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.35)] p-6 opacity-80"
        data-testid={testId}
        aria-disabled="true"
      >
        <div className="text-xs uppercase tracking-caps text-ink-tertiary">
          {eyebrow}
        </div>
        <h3 className="mt-3 text-[20px] font-semibold leading-snug text-ink-primary">
          {headline}
        </h3>
        <div className="mt-4 flex flex-1 items-end justify-between gap-3">
          <span className="text-[13px] italic text-ink-secondary">{meta}</span>
          {stateLabel && (
            <span
              className="text-[12px] italic text-ink-tertiary"
              data-testid={`${testId}-state-label`}
            >
              {stateLabel}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex h-full flex-col rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.55)] p-6 backdrop-blur-[20px] transition-all duration-base ease-out hover:-translate-y-1 hover:border-active-border hover:shadow-elev-lg"
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-caps text-ink-tertiary">
        {eyebrow}
      </div>
      <h3 className="mt-3 text-[20px] font-semibold leading-snug text-ink-primary">
        {headline}
      </h3>
      <div className="mt-4 flex flex-1 items-end justify-between gap-3">
        <span className="text-[13px] italic text-ink-secondary">{meta}</span>
        <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-frac-violet transition-transform duration-fast ease-out group-hover:translate-x-0.5">
          {readLabel}
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
        </span>
      </div>
      {stateLabel && (
        <div
          className="mt-3 text-[12px] italic text-ink-tertiary"
          data-testid={`${testId}-state-label`}
        >
          {stateLabel}
        </div>
      )}
    </a>
  );
}

function renderSegments(value, keyPrefix = "") {
  // String → render as text node.
  if (typeof value === "string") return value;
  // Array of segments → text / bold / external link.
  if (!Array.isArray(value)) return null;
  return value.map((seg, i) => {
    const k = `${keyPrefix}${i}`;
    if (!seg || typeof seg !== "object") {
      return <React.Fragment key={k}>{String(seg ?? "")}</React.Fragment>;
    }
    if (seg.href) {
      return (
        <a
          key={k}
          href={seg.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={seg.ariaLabel || undefined}
          className="text-frac-violet underline underline-offset-4 hover:text-[#8A1AE8] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frac-violet"
        >
          {seg.text}
        </a>
      );
    }
    if (seg.bold) {
      return (
        <strong key={k} className="font-semibold text-ink-primary">
          {seg.text}
        </strong>
      );
    }
    return <React.Fragment key={k}>{seg.text}</React.Fragment>;
  });
}

function AnnouncementCard({ item, testId, readMoreLabel, readLessLabel }) {
  const [open, setOpen] = useState(false);
  const isEditorial = item.thumbnailVariant === "editorial";
  return (
    <article
      className="flex h-full flex-col overflow-hidden rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.55)] backdrop-blur-[20px] transition-all duration-base ease-out hover:border-active-border hover:shadow-elev-lg"
      data-testid={testId}
      id={item.slug}
    >
      {/* Thumbnail plate — "emblem" variant is a surface-raised chip for
          white-mark award logos; "editorial" is a full-bleed cover for
          longform article hero cards. */}
      {isEditorial ? (
        <div className="relative h-56 w-full overflow-hidden border-b border-hairline">
          <img
            src={item.thumbnail}
            alt={item.thumbnailAlt || ""}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : (
        <div
          className="relative flex h-40 items-center justify-center border-b border-hairline bg-[var(--surface-raised)] px-6 py-4"
          aria-hidden
        >
          <img
            src={item.thumbnail}
            alt=""
            className="h-full max-h-32 w-auto object-contain"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-caps text-ink-tertiary">
          <span className="inline-flex items-center rounded-full border border-active-border/50 bg-frac-violet/10 px-2 py-0.5 text-[10px] font-semibold text-frac-violet">
            {item.category}
          </span>
          <span aria-hidden>·</span>
          <span>{item.date}</span>
        </div>

        <h3 className="mt-3 text-[20px] font-semibold leading-snug text-ink-primary">
          {item.headline}
        </h3>

        <p className="mt-3 text-[14px] leading-normal text-ink-secondary">
          {renderSegments(item.lead, "lead-")}
        </p>

        {open && (
          <div
            className="mt-4 space-y-3 border-t border-hairline pt-4 text-[14px] leading-normal text-ink-secondary"
            data-testid={`${testId}-body`}
          >
            {(item.body || []).map((para, i) => (
              <p key={i}>{renderSegments(para, `b${i}-`)}</p>
            ))}
            {item.author && (
              <p className="pt-1 text-[12px] italic text-ink-tertiary">
                {item.author}
              </p>
            )}
          </div>
        )}

        <div className="mt-auto pt-4">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={`${testId}-body`}
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-frac-violet transition-colors duration-fast ease-out hover:text-[#8A1AE8] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frac-violet"
            data-testid={`${testId}-toggle`}
          >
            {open ? readLessLabel : readMoreLabel}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-fast ease-out ${open ? "rotate-180" : ""}`}
              strokeWidth={2}
              aria-hidden
            />
          </button>
        </div>
      </div>
    </article>
  );
}

function Grid({ children, testId }) {
  return (
    <Stagger
      className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      gap={60}
      data-testid={testId}
    >
      {children}
    </Stagger>
  );
}

function Hero() {
  const { t } = useT("newsroom");
  return (
    <section
      className="container-frac pt-24 pb-12 md:pt-32 md:pb-16"
      data-testid="newsroom-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 max-w-2xl text-lg leading-normal text-ink-secondary">
            {t("hero.body")}{" "}
            <a
              href="mailto:media@frac.io"
              className="text-frac-violet underline underline-offset-4 hover:text-[#8A1AE8]"
            >
              media@frac.io
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function PressKitCta() {
  const { t } = useT("newsroom");
  return (
    <section
      className="container-frac pb-24 md:pb-32"
      data-testid="newsroom-press"
    >
      <div className="mt-16 border-t border-hairline pt-12">
        <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
              {t("press.eyebrow")}
            </div>
            <p className="mt-3 text-lg leading-normal text-ink-primary">
              {t("press.body")}{" "}
              <a
                href="mailto:media@frac.io"
                className="text-frac-violet underline underline-offset-4 hover:text-[#8A1AE8]"
              >
                media@frac.io
              </a>
              .
            </p>
          </div>
          <Button
            as="a"
            href="mailto:media@frac.io?subject=Press%20enquiry"
            variant="primary-on-dark"
            size="md"
            data-testid="newsroom-media-cta"
          >
            {t("press.cta")}
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function Newsroom() {
  const [tab, setTab] = useState("news");
  const { t, i18n } = useT("newsroom");
  const lang = (i18n && i18n.language && i18n.language.startsWith("zh")) ? "zh" : "en";
  const readLabel = t("labels.read");
  const readMoreLabel = t("labels.readMore");
  const readLessLabel = t("labels.readLess");
  const insightsEyebrow = t("labels.insightsEyebrow");
  const announcements = t("announcements", { returnObjects: true }) || [];
  const insightAnnouncements = t("insightAnnouncements", { returnObjects: true }) || [];

  // Resolve a card's locale-aware "label" field (archived / unavailable) and
  // its "source_suffix" (PR Newswire byline on three Masverse-origin cards).
  const resolveLabel = (card) => (card.label ? card.label[lang] : null);
  const resolveSuffix = (card) => (card.source_suffix ? card.source_suffix[lang] : "");

  return (
    <>
      <PageSeo
        title="Newsroom — What We're Building and Who's Noticing"
        description="Announcements, media coverage and thought leadership from Frac's team. For press enquiries, email media@frac.io."
        path="/newsroom"
        image="/og/newsroom.png"
      />
      <div data-testid="page-newsroom">
        <Hero />

        <section className="container-frac">
          <Reveal>
            <Tabs value={tab} onValueChange={setTab} onDark>
              <TabsList aria-label={t("tabs.aria")}>
                <TabsTrigger value="news" data-testid="newsroom-tab-news">
                  {t("tabs.news")}
                </TabsTrigger>
                <TabsTrigger value="media" data-testid="newsroom-tab-media">
                  {t("tabs.media")}
                </TabsTrigger>
                <TabsTrigger value="insights" data-testid="newsroom-tab-insights">
                  {t("tabs.insights")}
                </TabsTrigger>
              </TabsList>

              <div className="mt-10">
                <TabsPanel value="news">
                  <Grid testId="newsroom-news-grid">
                    {Array.isArray(announcements) &&
                      announcements.map((a, i) => (
                        <StaggerItem key={a.slug}>
                          <AnnouncementCard
                            item={a}
                            testId={`announcement-${a.slug}`}
                            readMoreLabel={readMoreLabel}
                            readLessLabel={readLessLabel}
                          />
                        </StaggerItem>
                      ))}
                    {NEWS.map((n, i) => (
                      <StaggerItem key={`${n.date}-${i}`}>
                        <ItemCard
                          eyebrow={n.date}
                          headline={n.headline}
                          meta={`${n.source}${resolveSuffix(n)}`}
                          url={n.url}
                          state={n.state}
                          label={resolveLabel(n)}
                          testId={`news-item-${i}`}
                          readLabel={readLabel}
                        />
                      </StaggerItem>
                    ))}
                  </Grid>
                </TabsPanel>

                <TabsPanel value="media">
                  <Grid testId="newsroom-media-grid">
                    {MEDIA.map((m, i) => (
                      <StaggerItem key={`${m.outlet}-${i}`}>
                        <ItemCard
                          eyebrow={m.date}
                          headline={m.headline}
                          meta={`${m.outlet}${resolveSuffix(m)}`}
                          url={m.url}
                          state={m.state}
                          label={resolveLabel(m)}
                          testId={`media-item-${i}`}
                          readLabel={readLabel}
                        />
                      </StaggerItem>
                    ))}
                  </Grid>
                </TabsPanel>

                <TabsPanel value="insights">
                  <Grid testId="newsroom-insights-grid">
                    {Array.isArray(insightAnnouncements) &&
                      insightAnnouncements.map((a) => (
                        <StaggerItem key={a.slug}>
                          <AnnouncementCard
                            item={a}
                            testId={`insight-announcement-${a.slug}`}
                            readMoreLabel={readMoreLabel}
                            readLessLabel={readLessLabel}
                          />
                        </StaggerItem>
                      ))}
                    {INSIGHTS.map((ins, i) => (
                      <StaggerItem key={`${ins.title}-${i}`}>
                        <ItemCard
                          eyebrow={insightsEyebrow}
                          headline={ins.title}
                          meta={`${ins.author}${resolveSuffix(ins)}`}
                          url={ins.url}
                          state={ins.state}
                          label={resolveLabel(ins)}
                          testId={`insight-item-${i}`}
                          readLabel={readLabel}
                        />
                      </StaggerItem>
                    ))}
                  </Grid>
                </TabsPanel>
              </div>
            </Tabs>
          </Reveal>
        </section>

        <PressKitCta />
      </div>
    </>
  );
}
